import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
 addDays, differenceInDays, endOfMonth, format,
 eachMonthOfInterval, eachWeekOfInterval
} from "date-fns";
import axios from "axios";
import { toast } from "sonner";
import { FileText, HardHat, Wrench, ClipboardCheck } from "lucide-react";

const ROW_H = 150;
const HEADER_H = 52;
const BAR_H = 30;
const BAR_Y = 38;
const DIAMOND = 8;
const LABEL_Y = BAR_Y + BAR_H + 16;
const PAD_L = 40;
const MIN_LABEL_GAP = 72;

const PHASE_ICON = { 1: FileText, 2: HardHat, 3: Wrench, 4: ClipboardCheck };

const COLORS = {
 1: { m: "#f97316", bg: "rgba(249,115,22,0.10)", bd: "rgba(249,115,22,0.28)", dm: "#fb923c", dbg: "rgba(251,146,60,0.12)", dbd: "rgba(251,146,60,0.30)" },
 2: { m: "#ef4444", bg: "rgba(239,68,68,0.10)", bd: "rgba(239,68,68,0.28)", dm: "#f87171", dbg: "rgba(248,113,113,0.12)", dbd: "rgba(248,113,113,0.30)" },
 3: { m: "#3b82f6", bg: "rgba(59,130,246,0.10)", bd: "rgba(59,130,246,0.28)", dm: "#60a5fa", dbg: "rgba(96,165,250,0.12)", dbd: "rgba(96,165,250,0.30)" },
 4: { m: "#10b981", bg: "rgba(16,185,129,0.10)", bd: "rgba(16,185,129,0.28)", dm: "#34d399", dbg: "rgba(52,211,153,0.12)", dbd: "rgba(52,211,153,0.30)" },
};

const STAT_COLORS = {
 completed: { c: "#10b981", dc: "#34d399" },
 in_progress: { c: "#3b82f6", dc: "#60a5fa" },
 not_started: { c: "#94a3b8", dc: "#64748b" },
};

const TimelineView = ({ jobs, phaseNames, phaseWeights, selectedProject, onJobsReload, translateJobName }) => {
 const { t } = useTranslation();
 const containerRef = useRef(null);
 const scrollRef = useRef(null);
 const [tooltip, setTooltip] = useState(null);
 const [isDark, setIsDark] = useState(false);
 const [dragState, setDragState] = useState(null);
 const [cWidth, setCWidth] = useState(1300);

 useEffect(() => {
 const check = () => setIsDark(document.documentElement.classList.contains("dark"));
 check();
 const obs = new MutationObserver(check);
 obs.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
 return () => obs.disconnect();
 }, []);

 // Measure container width
 useEffect(() => {
 const el = scrollRef.current;
 if (!el) return;
 setCWidth(el.clientWidth);
 const ro = new ResizeObserver(entries => {
 if (entries[0]) setCWidth(entries[0].contentRect.width);
 });
 ro.observe(el);
 return () => ro.disconnect();
 }, []);

 // ===== STEP 1: Compute timeline range =====
 const { tStart, tEnd, totalDays } = useMemo(() => {
 const realDates = jobs
 .flatMap(j => [j.start_date, j.completion_date].filter(Boolean))
 .map(d => new Date(d));

 let tStart, tEnd;
 if (realDates.length > 0) {
 tStart = addDays(new Date(Math.min(...realDates)), -14);
 tEnd = addDays(new Date(Math.max(...realDates)), 30);
 } else {
 tStart = new Date();
 tEnd = addDays(tStart, 240);
 }
 if (differenceInDays(tEnd, tStart) < 200) tEnd = addDays(tStart, 200);
 return { tStart, tEnd, totalDays: differenceInDays(tEnd, tStart) };
 }, [jobs]);

 // ===== STEP 2: Dynamic day width to fit viewport =====
 const dayW = useMemo(() => {
 const ideal = (cWidth - PAD_L * 2) / totalDays;
 return Math.max(5, Math.min(24, ideal));
 }, [cWidth, totalDays]);

 // Helper: date to X
 const dToX = useCallback((d) => PAD_L + differenceInDays(d, tStart) * dayW, [tStart, dayW]);

 // ===== STEP 3: Compute phases, positions, headers =====
 const { phases, totalW, months, weeks, depLines, visibleLabels } = useMemo(() => {
 const totalW = PAD_L * 2 + totalDays * dayW;

 // Group jobs by phase
 const groups = {};
 jobs.forEach(j => { if (!groups[j.phase]) groups[j.phase] = []; groups[j.phase].push(j); });

 // Normalized weights
 const activePhases = [1, 2, 3, 4].filter(p => groups[p]?.length);
 const rawW = {};
 activePhases.forEach(p => { rawW[p] = Math.max(phaseWeights[p] || 25, (groups[p]?.length || 0) * 2); });
 const totalRawW = Object.values(rawW).reduce((a, b) => a + b, 0);

 let cumW = 0;
 const phases = activePhases.map(p => {
 const pJobs = groups[p];
 const normW = (rawW[p] / totalRawW) * 100;
 const pStart = addDays(tStart, Math.floor(totalDays * cumW / 100) + 7);
 cumW += normW;
 const pEnd = addDays(tStart, Math.floor(totalDays * cumW / 100) - 3);
 const dur = Math.max(differenceInDays(pEnd, pStart), 14);

 const tasks = pJobs.map((job, i) => {
 const td = job.start_date ? new Date(job.start_date)
 : addDays(pStart, Math.floor((i + 0.5) / pJobs.length * dur));
 return { ...job, cDate: td, x: PAD_L + differenceInDays(td, tStart) * dayW, hasReal: !!job.start_date };
 });

 const xs = tasks.map(t => t.x);
 const minX = Math.min(...xs), maxX = Math.max(...xs);
 const barL = minX - 30, barR = maxX + 50, barW = barR - barL;

 const comp = pJobs.filter(j => j.status === "completed").length;
 const prog = pJobs.filter(j => j.status === "in_progress").length;
 const tot = pJobs.length;
 const pct = tot > 0 ? comp / tot : 0;
 const st = comp === tot ? "completed" : (comp > 0 || prog > 0) ? "in_progress" : "not_started";

 return { phase: p, name: phaseNames[p], tasks, barL, barR, barW, pct, st, comp, tot, normW };
 });

 // Headers
 const months = eachMonthOfInterval({ start: tStart, end: tEnd }).map(d => ({
 label: format(d, "MMM").toUpperCase(),
 x: PAD_L + differenceInDays(d > tStart ? d : tStart, tStart) * dayW,
 w: (PAD_L + differenceInDays(endOfMonth(d) > tEnd ? tEnd : endOfMonth(d), tStart) * dayW) -
 (PAD_L + differenceInDays(d > tStart ? d : tStart, tStart) * dayW)
 }));
 const weeks = eachWeekOfInterval({ start: tStart, end: tEnd }, { weekStartsOn: 1 }).map(d => ({
 label: format(d, "d"), x: PAD_L + differenceInDays(d, tStart) * dayW
 }));

 // Dependencies
 const depLines = [];
 const allT = phases.flatMap((p, pi) => p.tasks.map(t => ({ ...t, pi })));
 for (const t of allT) {
 if (!t.dependencies?.length) continue;
 for (const dn of t.dependencies) {
 const src = allT.find(s => s.job_name === dn);
 if (src) depLines.push({ sx: src.x, sy: src.pi * ROW_H + BAR_Y + BAR_H + 4, tx: t.x, ty: t.pi * ROW_H + BAR_Y - 4 });
 }
 }

 // Visible labels
 const visibleLabels = {};
 phases.forEach(p => {
 const vis = new Set();
 let lastX = -Infinity;
 [...p.tasks].sort((a, b) => a.x - b.x).forEach(t => {
 if (t.x - lastX >= MIN_LABEL_GAP) { vis.add(t.id); lastX = t.x; }
 });
 visibleLabels[p.phase] = vis;
 });

 return { phases, totalW, months, weeks, depLines, visibleLabels };
 }, [jobs, phaseNames, phaseWeights, tStart, tEnd, totalDays, dayW]);

 // ===== DRAG =====
 useEffect(() => {
 if (!dragState) return;
 const onMove = (e) => setDragState(prev => prev ? { ...prev, curX: e.clientX } : null);
 const onUp = async () => {
 if (dragState) {
 const dx = dragState.curX - dragState.startX;
 const dd = Math.round(dx / dayW);
 if (Math.abs(dd) > 0) {
 const nd = addDays(dragState.origDate, dd);
 try {
 await axios.patch(`/project-progress/${dragState.taskId}`, { start_date: nd.toISOString() });
 toast.success(t("timeline.dateUpdated"));
 onJobsReload?.();
 } catch { toast.error(t("timeline.failedToUpdateDate")); }
 }
 }
 setDragState(null);
 };
 window.addEventListener("mousemove", onMove);
 window.addEventListener("mouseup", onUp);
 return () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
 }, [dragState, onJobsReload, dayW]);

 const handleMDown = useCallback((e, task) => {
 e.preventDefault();
 setDragState({ taskId: task.id, startX: e.clientX, curX: e.clientX, origDate: task.cDate, origX: task.x });
 }, []);

 // Color helpers
 const cl = (phase) => {
 const c = COLORS[phase] || COLORS[1];
 return isDark ? { m: c.dm, bg: c.dbg, bd: c.dbd } : { m: c.m, bg: c.bg, bd: c.bd };
 };
 const sc = (status) => {
 const s = STAT_COLORS[status] || STAT_COLORS.not_started;
 return isDark ? s.dc : s.c;
 };

 const totalH = phases.length * ROW_H + 40;
 const todayX = PAD_L + differenceInDays(new Date(), tStart) * dayW;
 const dragDx = dragState ? dragState.curX - dragState.startX : 0;

 return (
 <div ref={containerRef} className="relative border border-gray-200 rounded-xl overflow-hidden bg-white#0a0a0f]" data-testid="timeline-view">
 <div ref={scrollRef} className="overflow-x-auto overflow-y-auto" style={{ maxHeight: "70vh" }}>
 <div style={{ width: totalW, minWidth: "100%" }}>

 {/* HEADER */}
 <div className="sticky top-0 z-20 bg-white/95#0a0a0f]/95 backdrop-blur-sm border-b border-gray-100" style={{ height: HEADER_H }}>
 <div className="relative h-6 border-b border-gray-50">
 {months.map((m, i) => (
 <div key={i} className="absolute top-0 h-full flex items-center text-[11px] font-semibold text-slate-400 tracking-widest uppercase" style={{ left: m.x, width: m.w }}>
 <span className="pl-2">{m.label}</span>
 </div>
 ))}
 </div>
 <div className="relative h-[26px]">
 {weeks.map((w, i) => (
 <div key={i} className="absolute top-0 h-full flex items-center text-[11px] text-slate-300 font-medium" style={{ left: w.x, width: dayW * 7 }}>
 <span className="pl-1.5">{w.label}</span>
 </div>
 ))}
 </div>
 </div>

 {/* BODY */}
 <div className="relative" style={{ height: totalH }}>

 {/* Grid + Today + Dependencies SVG */}
 <svg className="absolute inset-0 pointer-events-none" width={totalW} height={totalH}>
 {weeks.map((w, i) => (
 <line key={i} x1={w.x} y1={0} x2={w.x} y2={totalH}
 stroke={isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.04)"} strokeWidth={1} />
 ))}
 <line x1={todayX} y1={0} x2={todayX} y2={totalH} stroke="#ef4444" strokeWidth={1.5} strokeDasharray="4 3" opacity={0.5} />
 <circle cx={todayX} cy={6} r={3} fill="#ef4444" opacity={0.7} />

 {depLines.map((l, i) => {
 const my = (l.sy + l.ty) / 2;
 return (
 <path key={i}
 d={`M ${l.sx} ${l.sy} C ${l.sx} ${my}, ${l.tx} ${my}, ${l.tx} ${l.ty}`}
 fill="none" stroke={isDark ? "rgba(148,163,184,0.25)" : "rgba(148,163,184,0.40)"} strokeWidth={1.5}
 />
 );
 })}
 </svg>

 {/* PHASE ROWS */}
 {phases.map((p, pi) => {
 const colors = cl(p.phase);
 const rowY = pi * ROW_H;
 const Icon = PHASE_ICON[p.phase] || FileText;

 return (
 <div key={p.phase} className="absolute left-0 right-0" style={{ top: rowY, height: ROW_H }} data-testid={`timeline-phase-${p.phase}`}>
 {pi > 0 && <div className="absolute left-0 right-0 top-0 h-px bg-gray-100" />}

 {/* Phase title */}
 <div className="absolute flex items-center gap-2" style={{ left: p.barL, top: 8 }}>
 <Icon className="w-3.5 h-3.5" style={{ color: colors.m }} />
 <span className="text-[13px] font-semibold text-slate-700 whitespace-nowrap">{p.name}</span>
 <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full" style={{
 background: colors.bg, color: colors.m, border: `1px solid ${colors.bd}`
 }}>{p.comp}/{p.tot}</span>
 </div>

 {/* Bar */}
 <div className="absolute" data-testid={`timeline-bar-${p.phase}`} style={{
 left: p.barL, top: BAR_Y, width: p.barW, height: BAR_H,
 borderRadius: BAR_H / 2,
 border: `1.5px ${p.st === "not_started" ? "dashed" : "solid"} ${p.st === "not_started" ? (isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)") : colors.bd}`,
 background: p.st === "not_started" ? (isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)") : "transparent",
 overflow: "hidden",
 }}>
 {p.pct > 0 && (
 <div style={{
 position: "absolute", left: 0, top: 0, height: "100%",
 width: `${Math.min(p.pct * 100, 100)}%`,
 borderRadius: BAR_H / 2,
 background: `linear-gradient(90deg, ${colors.bg}, ${colors.m}22)`,
 }} />
 )}
 {p.st === "in_progress" && p.pct < 1 && (
 <div style={{
 position: "absolute", left: `${p.pct * 100}%`, top: 0, height: "100%",
 width: `${(1 - p.pct) * 100}%`,
 background: `linear-gradient(90deg, ${colors.m}25, ${colors.m}08)`,
 }} />
 )}
 </div>

 {/* Milestone diamonds */}
 {p.tasks.map(task => {
 const dx = dragState?.taskId === task.id ? dragDx : 0;
 const mx = task.x + dx;
 const filled = task.status !== "not_started";
 const col = sc(task.status);
 return (
 <div key={task.id}
 className="absolute cursor-grab active:cursor-grabbing z-10 group"
 style={{ left: mx - DIAMOND, top: BAR_Y + BAR_H / 2 - DIAMOND, width: DIAMOND * 2, height: DIAMOND * 2 }}
 onMouseDown={(e) => handleMDown(e, task)}
 onMouseEnter={(e) => {
 if (dragState) return;
 const r = containerRef.current?.getBoundingClientRect();
 if (!r) return;
 setTooltip({ x: e.clientX - r.left + 16, y: e.clientY - r.top - 12, task, phaseName: p.name });
 }}
 onMouseLeave={() => { if (!dragState) setTooltip(null); }}
 data-testid={`timeline-milestone-${task.id}`}
 >
 <div className="w-full h-full transition-transform duration-150 group-hover:scale-[1.4]" style={{
 transform: "rotate(45deg)",
 background: filled ? col : "transparent",
 border: `2px solid ${col}`,
 borderRadius: 2,
 }} />
 </div>
 );
 })}

 {/* Labels */}
 {p.tasks.map(task => {
 if (!visibleLabels[p.phase]?.has(task.id)) return null;
 const dx = dragState?.taskId === task.id ? dragDx : 0;
 const name = translateJobName ? translateJobName(task.job_name) : task.job_name;
 const short = name.length > 16 ? name.substring(0, 14) + "\u2026" : name;
 return (
 <div key={`lb-${task.id}`}
 className="absolute text-[10px] text-slate-400 whitespace-nowrap pointer-events-none select-none"
 style={{ left: task.x + dx, top: LABEL_Y, transform: "translateX(-50%)" }}
 >{short}</div>
 );
 })}
 </div>
 );
 })}
 </div>
 </div>
 </div>

 {/* TOOLTIP */}
 {tooltip && !dragState && (
 <div className="absolute z-50 bg-white border border-gray-200 rounded-lg shadow-xl px-3 py-2.5 pointer-events-none max-w-[260px]"
 style={{ left: Math.min(tooltip.x, (containerRef.current?.offsetWidth || 600) - 270), top: tooltip.y }}
 data-testid="timeline-tooltip"
 >
 <div className="text-[12px] font-semibold text-slate-800 leading-tight">
 {translateJobName ? translateJobName(tooltip.task.job_name) : tooltip.task.job_name}
 </div>
 <div className="text-[10px] text-slate-400 mt-0.5">{tooltip.phaseName}</div>
 <div className="flex items-center gap-1.5 mt-1.5">
 <span className="w-2 h-2 rounded-full" style={{ background: sc(tooltip.task.status) }} />
 <span className="text-[10px] font-medium text-slate-600 capitalize">
 {t(`timeline.statusLabels.${tooltip.task.status}`, tooltip.task.status.replace("_", " "))}
 </span>
 </div>
 {tooltip.task.start_date && (
 <div className="text-[10px] text-slate-400 mt-1">
 {t("timeline.start")}: {format(new Date(tooltip.task.start_date), "MMM d, yyyy")}
 </div>
 )}
 {tooltip.task.completion_date && (
 <div className="text-[10px] text-slate-400">
 {t("timeline.end")}: {format(new Date(tooltip.task.completion_date), "MMM d, yyyy")}
 </div>
 )}
 {tooltip.task.partner_id && (
 <div className="text-[10px] text-slate-400 mt-0.5 italic">{t("timeline.assignedToPartner")}</div>
 )}
 <div className="text-[9px] text-slate-300 mt-1.5 border-t border-gray-100 pt-1">
 {t("timeline.dragToChangeDate")}
 </div>
 </div>
 )}
 </div>
 );
};

export default TimelineView;
